// Stumble Wrapped — single-poster recap collage (1080×1920 PNG).
//
// Per ADR-0017 Tier 1: deterministic, browser-canvas. No AI, no Satori, no
// server roundtrip — the user taps Generate and a shareable PNG lands in <2s.
// Same Canvas + seededRandom plumbing as Stumble Cam (ADR-0016).
//
// Ported visually from `screens-recap.jsx → WrappedRecap` but rebuilt for the
// poster aspect ratio (1080×1920 story format) instead of in-app 375×634.

import { canvasToBlob, makeCanvas } from './canvasExport'
import {
  type DrinkKind,
  RECAP_PALETTE,
  drinkIconSvg,
  stampSvg,
  svgToImage,
  wordmarkSvg,
} from './recapAssets'
import {
  type ScrapbookLeaderboardEntry,
  type ScrapbookVenue,
  generateScrapbookCollage,
} from './recapLayouts/scrapbook'
import { seededRandom } from './seededRandom'

export type RecapMetadata = {
  nightId: string
  nightDate: Date
  /** Wall-clock start of the night. Defaults to nightDate when unset. */
  startedAt?: Date
  /** Wall-clock end of the night. null = still open / unknown. */
  endedAt?: Date | null
  venues: {
    name: string
    arrivedAt: Date
    leftAt: Date | null
    /** Optional URL to a selfie at this venue — drawn by the scrapbook variant. */
    photoUrl?: string | null
  }[]
  drinkTotals: { beer: number; wine: number; cocktail: number; shot: number }
  participants?: string[] // post-MVP teams; ignored for solo
  squadName?: string | null
  /** Friend leaderboard for the night — used by the scrapbook variant only. */
  leaderboard?: ScrapbookLeaderboardEntry[]
  /**
   * F3: explicit per-selfie array. The scrapbook used to collect photo sources
   * from `venues[].photoUrl` (one per venue), which collapsed N selfies at the
   * same venue into a single polaroid — user reported "3 selfies, 2 polaroids".
   * Routes now pass every selfie here directly. Most recent first, capped at
   * the layout's 6 slots downstream.
   */
  selfies?: {
    url: string
    venueName: string
    capturedAt: string // ISO timestamp
  }[]
}

export type RecapVariant = 'wrapped' | 'scrapbook' | 'flipbook'

export type RecapOpts = {
  /**
   * Recap layout variant. Default is `scrapbook` per ADR-0017 (2026-05-17
   * amendment) — richer for sharing, includes per-venue polaroids. `wrapped`
   * is the stat-poster fallback; `flipbook` is reserved for a follow-up PR
   * and falls back to scrapbook for now.
   */
  variant?: RecapVariant
  format?: 'image/png'
}

// 1080 × 1920 is the canonical story format (Instagram, TikTok). Locked.
const POSTER_W = 1080
const POSTER_H = 1920

/**
 * Render the Stumble Wrapped recap as a 1080×1920 PNG.
 *
 * @param photos  Reserved for the upcoming contact-sheet integration. Pass [].
 *                The Tier 1 wrapped poster is text/stat-driven and does not
 *                composite the night's photos yet (deferred to the scrapbook
 *                variant, per ADR-0017).
 * @param metadata Night summary — venues, drink totals, etc.
 * @param opts    `variant` is documented but only `'wrapped'` is built in this
 *                PR. Other values fall through to wrapped rendering.
 */
export async function generateRecapCollage(
  // photos: passed through to the scrapbook layout (which composites them into
  // polaroids). Ignored by the wrapped layout. Pass [] to use only the URL
  // photos attached to RecapMetadata.venues[].photoUrl.
  photos: Blob[],
  metadata: RecapMetadata,
  opts: RecapOpts = {},
): Promise<Blob> {
  // Default = scrapbook per ADR-0017 amendment (2026-05-17). Flipbook is
  // reserved (no canvas implementation yet) and falls back to scrapbook.
  const variant: RecapVariant = opts.variant ?? 'scrapbook'
  if (variant === 'scrapbook' || variant === 'flipbook') {
    return generateScrapbookCollage(photos, toScrapbookMetadata(metadata), {
      format: opts.format,
    })
  }
  // Wrapped — original poster.
  void photos
  const rand = seededRandom(`recap:${metadata.nightId}`)

  // Pre-load all SVG assets in parallel — drawImage needs decoded bitmaps.
  const [wordmark, stumbledStamp, wrappedStamp, beerIcon, wineIcon, cocktailIcon, shotIcon] =
    await Promise.all([
      svgToImage(wordmarkSvg({ width: 720, fill: RECAP_PALETTE.INK })),
      svgToImage(stampSvg('stumbledHere', { rotate: jitter(rand, 12) })),
      svgToImage(stampSvg('wrapped', { rotate: jitter(rand, 4) })),
      svgToImage(drinkIconSvg('beer')),
      svgToImage(drinkIconSvg('wine')),
      svgToImage(drinkIconSvg('cocktail')),
      svgToImage(drinkIconSvg('shot')),
    ])

  const canvas = makeCanvas(POSTER_W, POSTER_H)
  const ctx = canvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null
  if (!ctx) {
    throw new Error('generateRecapCollage: 2d context unavailable')
  }

  // ── Background ───────────────────────────────────────────────────────────
  // Vertical pink → deep-pink gradient. Mirrors `wrap-card theme-pink` from
  // the in-app preview but at story scale.
  const bg = ctx.createLinearGradient(0, 0, 0, POSTER_H)
  bg.addColorStop(0, '#f37cba')
  bg.addColorStop(0.65, RECAP_PALETTE.PINK)
  bg.addColorStop(1, '#c93f8b')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, POSTER_W, POSTER_H)

  // Paper-coloured inset frame so the whole thing reads as a poster, not a
  // browser screenshot. Inset = 36px on each side.
  const inset = 36
  ctx.strokeStyle = RECAP_PALETTE.PAPER
  ctx.lineWidth = 6
  roundRect(ctx, inset, inset, POSTER_W - inset * 2, POSTER_H - inset * 2, 28)
  ctx.stroke()

  // ── Header: wordmark + WRAPPED stamp ─────────────────────────────────────
  // Wordmark sits top-left within the frame. We draw it onto a paper-coloured
  // pill so the black letters stay legible over the pink.
  const headerY = 120
  const wordmarkScale = 0.62
  const wordmarkW = wordmark.width * wordmarkScale
  const wordmarkH = wordmark.height * wordmarkScale
  // Pill background
  ctx.fillStyle = RECAP_PALETTE.PAPER
  roundRect(ctx, 96, headerY - 18, wordmarkW + 80, wordmarkH + 24, 24)
  ctx.fill()
  ctx.drawImage(wordmark, 96 + 40, headerY - 8, wordmarkW, wordmarkH)

  // WRAPPED stamp anchored top-right, slightly tilted
  const wrW = 320
  const wrH = (wrappedStamp.height / wrappedStamp.width) * wrW
  ctx.drawImage(wrappedStamp, POSTER_W - wrW - 96, headerY - 12, wrW, wrH)

  // ── Eyebrow line under header ────────────────────────────────────────────
  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.font = "500 36px 'Special Elite','Courier New',monospace"
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText('YOUR NIGHT, THE SHORT VERSION', 96, headerY + wordmarkH + 56)

  // ── Big number: total drinks ────────────────────────────────────────────
  const totalDrinks =
    metadata.drinkTotals.beer +
    metadata.drinkTotals.wine +
    metadata.drinkTotals.cocktail +
    metadata.drinkTotals.shot
  const centerY = POSTER_H * 0.36

  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // Bagel Fat One is loaded via canon.css — fallback chain keeps render
  // deterministic across browsers even if the webfont fails to swap.
  ctx.font = "400 420px 'Bagel Fat One','Atkinson Hyperlegible',system-ui,sans-serif"
  ctx.fillText(String(totalDrinks), POSTER_W / 2, centerY)

  ctx.font = "400 88px 'Bagel Fat One','Atkinson Hyperlegible',system-ui,sans-serif"
  ctx.fillText(totalDrinks === 1 ? 'drink' : 'drinks', POSTER_W / 2, centerY + 240)

  // ── Stat row: 4 drink types with icons ──────────────────────────────────
  const statRowY = centerY + 420
  const cellWidth = (POSTER_W - inset * 2 - 64) / 4
  const cellHeight = 220
  const icons: { kind: DrinkKind; img: HTMLImageElement }[] = [
    { kind: 'beer', img: beerIcon },
    { kind: 'wine', img: wineIcon },
    { kind: 'cocktail', img: cocktailIcon },
    { kind: 'shot', img: shotIcon },
  ]

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  for (let i = 0; i < icons.length; i++) {
    const cell = icons[i]
    if (!cell) continue
    const { kind, img } = cell
    const x = inset + 32 + cellWidth * i
    const count = drinkCountOf(metadata.drinkTotals, kind)

    // Paper pill background per cell
    ctx.fillStyle = RECAP_PALETTE.PAPER
    roundRect(ctx, x + 6, statRowY, cellWidth - 12, cellHeight, 22)
    ctx.fill()

    // Icon (scaled to ~120px tall)
    const iconH = 124
    const iconW = (img.width / img.height) * iconH
    ctx.drawImage(img, x + (cellWidth - iconW) / 2, statRowY + 14, iconW, iconH)

    // Count
    ctx.fillStyle = RECAP_PALETTE.INK
    ctx.font = "400 64px 'Bagel Fat One',sans-serif"
    ctx.fillText(`× ${count}`, x + cellWidth / 2, statRowY + cellHeight - 36)
  }

  // ── Venue route: vertical list with arrow connectors ────────────────────
  const routeStartY = statRowY + cellHeight + 96
  const routeTitle = 'the route'
  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.font = "400 56px 'Bagel Fat One',sans-serif"
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(routeTitle, 120, routeStartY)

  const maxVenues = 4
  const venueRowH = 88
  const shown = metadata.venues.slice(0, maxVenues)
  for (let i = 0; i < shown.length; i++) {
    const v = shown[i]
    if (!v) continue
    const y = routeStartY + 48 + i * venueRowH

    // Bullet dot
    ctx.fillStyle = RECAP_PALETTE.PAPER
    ctx.beginPath()
    ctx.arc(140, y + 28, 12, 0, Math.PI * 2)
    ctx.fill()

    // Connector line down to next bullet
    if (i < shown.length - 1) {
      ctx.strokeStyle = RECAP_PALETTE.PAPER
      ctx.lineWidth = 4
      ctx.setLineDash([10, 10])
      ctx.beginPath()
      ctx.moveTo(140, y + 40)
      ctx.lineTo(140, y + venueRowH + 20)
      ctx.stroke()
      ctx.setLineDash([])
    }

    // Venue name
    ctx.fillStyle = RECAP_PALETTE.PAPER
    ctx.font = "400 56px 'Bagel Fat One',sans-serif"
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    const truncated = truncate(v.name, 22)
    ctx.fillText(truncated, 184, y + 32)
  }

  if (metadata.venues.length > maxVenues) {
    const moreCount = metadata.venues.length - maxVenues
    ctx.fillStyle = RECAP_PALETTE.PAPER
    ctx.font = "500 32px 'Special Elite','Courier New',monospace"
    ctx.fillText(`+ ${moreCount} more`, 184, routeStartY + 48 + shown.length * venueRowH + 16)
  }

  // ── "Stumbled here" sticker — bottom-left, tilted ───────────────────────
  const stickerSize = 260
  const stickerX = 96
  const stickerY = POSTER_H - inset - stickerSize - 200
  // Apply small per-night tilt from seededRandom (-3° to +3°)
  ctx.save()
  ctx.translate(stickerX + stickerSize / 2, stickerY + stickerSize / 2)
  ctx.rotate((jitter(rand, 3) * Math.PI) / 180)
  ctx.drawImage(stumbledStamp, -stickerSize / 2, -stickerSize / 2, stickerSize, stickerSize)
  ctx.restore()

  // ── Date stamp — bottom-right in Special Elite ──────────────────────────
  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.font = "500 44px 'Special Elite','Courier New',monospace"
  ctx.textAlign = 'right'
  ctx.textBaseline = 'alphabetic'
  const dateLine = metadata.nightDate.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  ctx.fillText(dateLine.toUpperCase(), POSTER_W - 120, POSTER_H - 120)

  // ── "stmbl.party" credit line — under date ─────────────────────────────
  ctx.font = "400 32px 'Special Elite','Courier New',monospace"
  ctx.fillText('stmbl.party', POSTER_W - 120, POSTER_H - 76)

  // ── Export ──────────────────────────────────────────────────────────────
  // Story posters are saved/shared as PNG so the IG/X compression doesn't
  // shred the bold display type. Quality arg is ignored for PNG.
  const format = opts.format ?? 'image/png'
  // canvasToBlob's typed surface is webp/jpeg — but the underlying
  // OffscreenCanvas.convertToBlob / HTMLCanvasElement.toBlob both accept PNG.
  return canvasToBlob(canvas, format as 'image/webp', 1)
}

// ── helpers ────────────────────────────────────────────────────────────────

function jitter(rand: () => number, amplitudeDeg: number): number {
  return (rand() * 2 - 1) * amplitudeDeg
}

function drinkCountOf(totals: RecapMetadata['drinkTotals'], kind: DrinkKind): number {
  switch (kind) {
    case 'beer':
      return totals.beer
    case 'wine':
      return totals.wine
    case 'cocktail':
      return totals.cocktail
    case 'shot':
      return totals.shot
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return `${s.slice(0, max - 1).trimEnd()}…`
}

/** Adapt the cross-variant RecapMetadata into the scrapbook generator's shape. */
function toScrapbookMetadata(meta: RecapMetadata): {
  nightId: string
  nightDate: Date
  startedAt: Date
  endedAt: Date | null
  venues: ScrapbookVenue[]
  drinkTotals: RecapMetadata['drinkTotals']
  participants?: string[]
  squadName?: string | null
  leaderboard?: ScrapbookLeaderboardEntry[]
  selfies?: { url: string; venueName: string; capturedAt: string }[]
} {
  return {
    nightId: meta.nightId,
    nightDate: meta.nightDate,
    startedAt: meta.startedAt ?? meta.nightDate,
    endedAt: meta.endedAt ?? null,
    venues: meta.venues.map((v) => ({
      name: v.name,
      arrivedAt: v.arrivedAt,
      leftAt: v.leftAt,
      photoUrl: v.photoUrl ?? null,
    })),
    drinkTotals: meta.drinkTotals,
    participants: meta.participants,
    squadName: meta.squadName ?? null,
    leaderboard: meta.leaderboard,
    selfies: meta.selfies,
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}
